import { Box } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { CoverageDeltaCell, CoverageTier, resolveCoverageTier } from "../../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { CoverageCellPopover } from "../CoverageCellPopover";
import { mapCalendarCells } from "../gridGeometry";
import { coverageRowLabel, type CoverageRow as RowType } from "../types";
import { type GridCellKey, useCoverageGridContext } from "./CoverageGridContext";
import { ExpandedCoverageCell } from "./ExpandedCoverageCell";
import { WeekDividerTrack } from "./WeekDividerTrack";

type Props = {
	row: RowType;
	isExpanded: boolean;
};

/**
 * The day-cell loop of a Coverage · Summary row — either the signed-deficit
 * `CoverageDeltaCell` (collapsed) or the read-only name-chips (`ExpandedCoverageCell`)
 * per day, with the single-open popover wrapping only the open/closing cell. Split
 * out of `CoverageRow`; grid-wide values come from context, so the only props are
 * the row and its expanded flag — keeping the component stable + memoizable.
 */
export const CoverageRowDays = memo(({ row, isExpanded }: Props) => {
	const { t } = useTranslation();
	const { weeks, dayDates, displayMode, editWiring, popover, onOpenRecurring } = useCoverageGridContext();
	// Job depts identify a row by its job (localized "Unassigned" for the sentinel); role label for non-job rows.
	const rowLabel = `${row.location} · ${coverageRowLabel(row, t)} · ${row.shift}`;
	// Recurring-shift editing is available unless blocked or the row has no backing
	// config (outside-config rows can't be mapped to a recurring shift to edit).
	const recurringEditable = !!editWiring && !editWiring.editBlocked && !row.outsideConfig;

	return (
		<>
			{mapCalendarCells(
				weeks,
				(w, _d, idx) => {
					const cell = row.days[idx];
					const date = dayDates[idx];
					const key: GridCellKey = { rowId: row.id, dayIdx: idx };
					const isOpen = popover.isOpen(key);
					// A closing cell keeps its wrapper mounted (isOpen=false) so the exit animation plays.
					const isClosing = popover.isClosing(key);
					const tier = resolveCoverageTier(cell);
					const dayLabel = date.toFormat("ccc, LLL d");
					const ariaLabel = `${rowLabel}, ${dayLabel}: ${tier === CoverageTier.NotRequired ? t("schedule.template.v3.notRequired") : t("schedule.template.v3.cellAria", { filled: cell.filled, target: cell.target })}`;
					const openCellToggle = () => popover.toggle(key);

					// The cell body is identical whether or not it's wrapped in a popover — so
					// anchoring/positioning stay the same. Only the open (or closing) cell mounts a
					// SystemReactTinyPopover; every other cell renders bare (no idle document listeners).
					const cellBody = isExpanded ? (
						<ExpandedCoverageCell cell={cell} weekIdx={w} ariaLabel={ariaLabel} dataTestId={`ms3-cell-${row.id}-${idx}`} onOpen={openCellToggle} />
					) : (
						<Box className="ms3-cell" role="gridcell" data-week={w} sx={{ width: "100%", height: "100%" }}>
							<CoverageDeltaCell
								filled={cell.filled}
								target={cell.target}
								minimum={cell.minimum}
								required={cell.required}
								// The MARKER is long-leave only (`ptoCount` also counts routine days off, which
								// the Weekly and Consecutive grids leave unmarked — this keeps the three in step).
								ptoCount={cell.longPtoCount}
								displayMode={displayMode}
								ariaLabel={ariaLabel}
								dataTestId={`ms3-cell-${row.id}-${idx}`}
								onClick={openCellToggle}
							/>
						</Box>
					);

					return isOpen || isClosing ? (
						<SystemReactTinyPopover
							isOpen={isOpen}
							onClose={popover.close}
							onClickOutside={popover.close}
							onExitComplete={() => popover.clearClosing(key)}
							positions={["top", "bottom"]}
							content={
								<CoverageCellPopover
									rowLabel={rowLabel}
									dayLabel={dayLabel}
									cell={cell}
									onClose={popover.close}
									onEditAssignment={recurringEditable ? (assignment) => onOpenRecurring({ mode: "edit", row, nurseId: assignment.nurseId }, idx) : undefined}
									onCreate={recurringEditable ? () => onOpenRecurring({ mode: "create", row }, idx) : undefined}
									editBlocked={editWiring?.editBlocked}
									editBlockedTooltip={editWiring?.editBlockedTooltip}
								/>
							}
							childrenWrapperStyle={{ display: "block", width: "100%", height: "100%" }}
						>
							{cellBody}
						</SystemReactTinyPopover>
					) : (
						cellBody
					);
				},
				() => (
					<WeekDividerTrack />
				),
			)}
		</>
	);
});
